// Use-case pages — EXAMPLES of how to use symbols in real contexts.
//
// Unlike /categories (a grid of every symbol of a type), a use case shows the
// symbols DOING A JOB: ready-to-copy snippets like "★★★★☆ (4/5)" or
// "Revenue ↑ 12%". The reader copies a whole example and swaps in their own
// words. Each page also lists the individual glyphs used, so they stay grabbable.
//
// SEO contract:
//   • slug MUST NOT equal any category slug (the build asserts this).
//   • <title> string MUST NOT be identical to any other route's static title.
//   • intro must be unique across pages, ≥ 280 chars (build-time dedupe check).

import { SYMBOL_SLUG_MAP } from "./symbol-name-map.generated";
import { getSymbolName } from "./symbol-names";

export interface UseCaseLink {
  kind: "category" | "script";
  slug: string;
  label: string;
}

export interface UseCaseExample {
  /** Ready-to-copy snippet that demonstrates the symbols in context. */
  text: string;
  /** What this example is / where it fits. */
  label: string;
}

export interface UseCase {
  slug: string;
  name: string;
  tagline: string;
  intro: string[];
  /** In-context, copy-paste example snippets — the heart of the page. */
  examples: UseCaseExample[];
  /** The individual glyphs used, for a grab-one reference row. */
  symbols: string[];
  related: UseCaseLink[];
}

export const useCases: UseCase[] = [
  {
    slug: "review-ratings",
    name: "Star rating examples",
    tagline: "Copy-paste star ratings and review marks — ★★★★☆, pros & cons, verdict lines.",
    intro: [
      "A row of filled and empty stars is the clearest way to show a score in plain text — it reads instantly, with no rating widget or image. The examples below are ready to copy: a perfect five, a four-and-a-half, a labelled \"rated\" line, even a quick pros-and-cons block.",
      "Grab any snippet and swap in your own numbers or words. Because these are plain Unicode characters, the rating looks identical in a product review, a comment, a spreadsheet cell or a chat message.",
    ],
    examples: [
      { text: "★★★★★", label: "Perfect score" },
      { text: "★★★★☆ (4/5)", label: "Four out of five" },
      { text: "★★★★½", label: "Half star" },
      { text: "Rated ★★★★☆ — 4.2/5", label: "Labelled rating line" },
      { text: "✓ Great value\n✗ Slow shipping", label: "Pros and cons" },
      { text: "Verdict: ♥ Recommended", label: "Verdict line" },
      { text: "☆☆☆☆☆ Not rated yet", label: "Empty / unrated" },
      { text: "Quality ★★★★★  ·  Price ★★★☆☆", label: "Multiple criteria" },
      { text: "★★★☆☆ (3/5)", label: "Three stars" },
      { text: "★☆☆☆☆ Poor", label: "One star" },
      { text: "⭐ 4.8 · 1,204 reviews", label: "Average with count" },
      { text: "Overall: ★★★★½", label: "Overall score" },
      { text: "♥♥♥♡♡ — 3/5 hearts", label: "Heart rating" },
      { text: "✔ Verified purchase", label: "Verified badge" },
      { text: "Would recommend ✓", label: "Recommendation" },
      { text: "Top pick ★", label: "Editor's pick" },
    ],
    symbols: ["★", "☆", "⭐", "✓", "✗", "♥", "♡", "↑", "↓", "✔", "✘"],
    related: [
      { kind: "category", slug: "star-symbols", label: "Stars" },
      { kind: "category", slug: "heart-symbols", label: "Hearts" },
    ],
  },
  {
    slug: "spreadsheet-markers",
    name: "Spreadsheet & status symbols",
    tagline: "Copy-paste status marks for cells — ✓ Done, ⚠ Review, Revenue ↑ 12%.",
    intro: [
      "Inside a spreadsheet cell or a status update, a tiny glyph reads faster than a word. These examples turn plain cells into a scannable dashboard: ticks and crosses for done and blocked, trend arrows for up and down, dots and stars for state and priority.",
      "Copy a snippet straight into a cell, a formula bar or a project tracker. Keep one convention down a column so a reader can skim it at a glance.",
    ],
    examples: [
      { text: "✓ Done", label: "Task complete" },
      { text: "✗ Blocked", label: "Blocked / failed" },
      { text: "⚠ Review needed", label: "Needs attention" },
      { text: "Revenue ↑ 12%", label: "Trending up" },
      { text: "Costs ↓ 8%", label: "Trending down" },
      { text: "✔ Pass  ·  ✘ Fail", label: "Pass / fail column" },
      { text: "★ High priority", label: "Priority flag" },
      { text: "● Active   ○ Inactive", label: "Status dots" },
      { text: "→ In progress", label: "In progress" },
      { text: "◆ Milestone", label: "Milestone" },
      { text: "Q1 ✓  Q2 ✓  Q3 ✗  Q4 …", label: "Quarter tracker" },
      { text: "Budget $5,000 ✓ on track", label: "Budget status" },
      { text: "⚠ Overdue", label: "Overdue flag" },
      { text: "✓ Yes   ✗ No   — N/A", label: "Yes / no / N/A" },
      { text: "Owner → Alex", label: "Assignment" },
      { text: "▲ +5   ▼ −3", label: "Net change" },
    ],
    symbols: ["✓", "✔", "✗", "✘", "⚠", "★", "↑", "↓", "→", "▲", "▼", "●", "○", "◆"],
    related: [
      { kind: "category", slug: "check-cross-symbols", label: "Check & Cross" },
      { kind: "category", slug: "arrow-symbols", label: "Arrows" },
    ],
  },
  {
    slug: "todo-checklist",
    name: "Checklist & to-do examples",
    tagline: "Copy-paste checklist lines — ☐ open, ☑ done, nested sub-tasks.",
    intro: [
      "When a real checkbox widget isn't available — a notes app, a README, an issue template — these characters build a working checklist in plain text. The examples cover open and ticked items, a bracket style, and a nested list with sub-tasks.",
      "Copy a line, type your task after it, and stack them. Pair an empty box with a filled one to show progress, and step down to a small bullet for sub-items.",
    ],
    examples: [
      { text: "☐ Buy groceries", label: "Open task" },
      { text: "☑ Send the invoice", label: "Completed task" },
      { text: "✓ Call the dentist", label: "Checked off" },
      { text: "✗ Cancelled meeting", label: "Dropped item" },
      { text: "[✓] Ship the release", label: "Bracket style" },
      { text: "• Plan the trip\n  ◦ Book flights\n  ◦ Reserve hotel", label: "Nested sub-tasks" },
      { text: "→ Up next: review the PR", label: "Next step" },
      { text: "★ Finish the deck (today)", label: "Priority item" },
      { text: "☑ Pay rent\n☑ Email landlord\n☐ Schedule repair", label: "Mini list" },
      { text: "✗ Skipped — out of stock", label: "Skipped with reason" },
      { text: "⚠ Due today", label: "Due flag" },
      { text: "→ Waiting on a reply", label: "Blocked / waiting" },
      { text: "1. ☐ Draft   2. ☐ Review   3. ☐ Send", label: "Numbered steps" },
      { text: "Today\n• Standup\n• Code review\n• Ship", label: "Day plan" },
      { text: "☒ Won't do", label: "Won't-do" },
      { text: "✓✓✓☐☐ — 3 of 5 done", label: "Progress count" },
    ],
    symbols: ["☐", "☑", "☒", "✓", "✗", "▢", "▸", "‣", "•", "◦", "→", "★", "⚠"],
    related: [
      { kind: "category", slug: "check-cross-symbols", label: "Check & Cross" },
      { kind: "category", slug: "bullet-symbols", label: "Bullets" },
    ],
  },
  {
    slug: "arrows-cta",
    name: "Arrow & call-to-action examples",
    tagline: "Copy-paste link text and pointers — Read more →, Sign up ➜, cause ⇒ effect.",
    intro: [
      "An arrow at the end of a link or button does a lot of work: it signals \"there's more this way\" without a single extra word. These examples show arrows in call-to-action text, navigation, step flows and logical implication.",
      "Copy a snippet and replace the words with your own. Keep a space between the text and the arrow so it reads as direction, not punctuation.",
    ],
    examples: [
      { text: "Read more →", label: "Inline link" },
      { text: "Sign up ➜", label: "Button label" },
      { text: "← Back", label: "Back navigation" },
      { text: "Swipe up ↑", label: "Gesture prompt" },
      { text: "cause ⇒ effect", label: "Implication" },
      { text: "Step 1 → Step 2 → Step 3", label: "Step flow" },
      { text: "See details »", label: "Continue marker" },
      { text: "Was here ↦ now here", label: "Maps-to / moved" },
      { text: "Learn more ➔", label: "CTA variant" },
      { text: "Get started →", label: "Onboarding" },
      { text: "↓ Scroll for more", label: "Scroll hint" },
      { text: "Home › Blog › Post", label: "Breadcrumb" },
      { text: "Before → After", label: "Comparison" },
      { text: "⟶ continue reading", label: "Continue" },
      { text: "« Prev    Next »", label: "Pager" },
      { text: "Tap here ➤", label: "Tap prompt" },
    ],
    symbols: ["→", "←", "↑", "↓", "⇒", "➜", "➔", "»", "«", "↦", "⟶", "➤"],
    related: [
      { kind: "category", slug: "arrow-symbols", label: "Arrows" },
    ],
  },
  {
    slug: "social-bio-kit",
    name: "Social media bio ideas",
    tagline: "Copy-paste bio decorations — coffee ♥ books, ✦ name ✦, sparkle framing.",
    intro: [
      "A symbol or two can give a profile bio or username a bit of personality. These examples show common patterns: interests separated by hearts, a name framed with sparkles, soft brackets around a tagline, and a small status line.",
      "Copy one and drop in your own words. A little goes a long way — one or two symbols usually catch the eye without making the text hard to read.",
    ],
    examples: [
      { text: "coffee ♥ books ♥ travel", label: "Interests, heart-separated" },
      { text: "✦ alex ✦", label: "Name between sparkles" },
      { text: "✧･ﾟ: dreamer :･ﾟ✧", label: "Sparkle framing" },
      { text: "˚ʚ♡ɞ˚ welcome to my page ˚ʚ♡ɞ˚", label: "Soft heart wrap" },
      { text: "designer → coffee addict", label: "Tagline with arrow" },
      { text: "✿ open to work ✿", label: "Status line" },
      { text: "☽ night owl ☼", label: "Celestial pair" },
      { text: "★彡 username 彡★", label: "Decorated handle" },
      { text: "♡ she/her ♡", label: "Pronouns" },
      { text: "✈ based in NYC", label: "Location" },
      { text: "✦ artist · dreamer · ☕ lover", label: "Multi-interest" },
      { text: "˗ˏˋ welcome ´ˎ˗", label: "Dashed flourish" },
      { text: "❀ link in bio ❀", label: "Link prompt" },
      { text: "☆.｡.:* hi *:.｡.☆", label: "Twinkle wrap" },
      { text: "⊹ good vibes ⊹", label: "Vibes" },
      { text: "♪ music lover ♪", label: "Music tag" },
    ],
    symbols: ["✦", "✧", "⋆", "★", "☆", "♡", "♥", "✿", "❀", "☽", "☼", "➜"],
    related: [
      { kind: "category", slug: "heart-symbols", label: "Hearts" },
      { kind: "category", slug: "star-symbols", label: "Stars" },
    ],
  },
  {
    slug: "finance-pricing",
    name: "Price & finance examples",
    tagline: "Copy-paste prices and trends — $49.99, was $50 → $30, ↑ 2.4%.",
    intro: [
      "Money and movement both read well as symbols. These examples pair currency signs with figures and trend arrows: a plain price, a sale shown as old-to-new, a percentage move, an approximate total.",
      "Copy a line and edit the numbers. The currency sign sits next to the amount and an arrow beside the change, so a result lands at a glance.",
    ],
    examples: [
      { text: "$49.99", label: "Plain price" },
      { text: "Was $50 → now $30", label: "Sale (old → new)" },
      { text: "20% OFF", label: "Discount" },
      { text: "Stock ↑ 2.4%", label: "Trending up" },
      { text: "$ · € · £ · ¥", label: "Multi-currency note" },
      { text: "★ Best value — €19/mo", label: "Highlighted plan" },
      { text: "≈ £75 total", label: "Approximate total" },
      { text: "₿ 0.05 (≈ $3,200)", label: "Crypto with estimate" },
      { text: "From $9/mo", label: "Starting price" },
      { text: "Save 30% →", label: "Savings CTA" },
      { text: "$1,299 · in stock ✓", label: "Price + stock" },
      { text: "Total: €128.40", label: "Cart total" },
      { text: "↑ +$2.10 today", label: "Daily change" },
      { text: "Free → $0", label: "Free tier" },
      { text: "≈ ¥15,000", label: "Approximate (yen)" },
      { text: "Budget ±10%", label: "Tolerance" },
    ],
    symbols: ["$", "€", "£", "¥", "₿", "¢", "₹", "₽", "%", "↑", "↓", "±", "≈"],
    related: [
      { kind: "category", slug: "currency-symbols", label: "Currency" },
      { kind: "category", slug: "math-symbols", label: "Math" },
    ],
  },
  {
    slug: "coding-docs",
    name: "Code & docs examples",
    tagline: "Copy-paste snippets for comments & docs — x ≤ y, input ⇒ output, ⌘ + ⏎.",
    intro: [
      "In a code comment or a technical doc, a symbol is often tighter than spelling it out. These examples show mapping and implication arrows, comparison operators, a pipeline, a done/todo note, and a keyboard shortcut.",
      "Copy a snippet into a comment, a commit message or a cheat sheet. Use → for \"returns\" or \"maps to\", and reach for the key glyphs only when documenting shortcuts.",
    ],
    examples: [
      { text: "// getUser(id) → User", label: "Returns / maps to" },
      { text: "if (x ≤ y) { … }", label: "Comparison" },
      { text: "assert a ≠ b", label: "Not equal" },
      { text: "input ⇒ transform ⇒ output", label: "Pipeline / flow" },
      { text: "// ✓ handled  ·  ✗ TODO", label: "Done / todo note" },
      { text: "∑ over n items", label: "Sum / aggregate" },
      { text: "Press ⌘ + ⏎ to run", label: "Keyboard shortcut" },
      { text: "«deprecated» — use v2", label: "Inline tag" },
      { text: "@param id → user ID", label: "Param doc" },
      { text: "TODO ✗ handle null", label: "Todo comment" },
      { text: "v1 ⇒ v2 migration", label: "Migration note" },
      { text: "n ≥ 0 && n ≤ 100", label: "Range constraint" },
      { text: "A ∪ B  ·  A ∩ B", label: "Set operations" },
      { text: "loop ↻ until done", label: "Loop note" },
      { text: "result ≈ 3.14", label: "Approximation" },
      { text: "deprecated ⚠ use v2", label: "Deprecation" },
    ],
    symbols: ["→", "⇒", "≠", "≤", "≥", "∑", "∞", "∪", "∩", "↻", "✓", "✗", "«", "»", "⌘", "⏎", "…"],
    related: [
      { kind: "category", slug: "math-symbols", label: "Math" },
      { kind: "category", slug: "bracket-symbols", label: "Brackets" },
    ],
  },
  {
    slug: "playlist-music",
    name: "Now-playing & playlist examples",
    tagline: "Copy-paste music status lines — ▶ Now playing, ♫ Song — Artist, on repeat ↻.",
    intro: [
      "Music notes and transport controls read instantly as \"music\" in plain text — no image needed. These examples dress up a now-playing status, a track line, a playlist title and a mini player.",
      "Copy a line and drop in your own track. Lead with a note or a play symbol, then add a control to show what's happening to the song.",
    ],
    examples: [
      { text: "▶ Now playing: Song — Artist", label: "Now playing" },
      { text: "♫ Midnight City — M83", label: "Track line" },
      { text: "on repeat ↻ ♪", label: "On repeat" },
      { text: "⏸ Paused", label: "Paused state" },
      { text: "⏭ Up next: track 4", label: "Up next" },
      { text: "♬ late-night focus ♬", label: "Playlist title" },
      { text: "♥ Added to favorites", label: "Liked a track" },
      { text: "⏮  ⏯  ⏭   ♫", label: "Mini player" },
      { text: "♫ Now Spinning ♫", label: "Vinyl vibe" },
      { text: "▶ Play   ⏸ Pause   ⏹ Stop", label: "Controls row" },
      { text: "♬ summer playlist · 24 songs", label: "Playlist meta" },
      { text: "♥ liked · ♫ now playing", label: "Status combo" },
      { text: "⏯ Resume", label: "Resume" },
      { text: "♪ ♫ ♪ vibing", label: "Vibing" },
      { text: "⏮ Restart track", label: "Restart" },
      { text: "♬ ✦ Top 50 ✦ ♬", label: "Chart title" },
    ],
    symbols: ["♪", "♫", "♬", "♩", "▶", "⏸", "⏯", "⏭", "⏮", "★", "♥", "↻"],
    related: [
      { kind: "category", slug: "music-symbols", label: "Music" },
      { kind: "category", slug: "technical-symbols", label: "Tech & UI" },
    ],
  },
  {
    slug: "astrology-kit",
    name: "Astrology & horoscope examples",
    tagline: "Copy-paste astrology lines — ♌ Leo, Sun ☉ · Moon ☽, ♀ in ♎.",
    intro: [
      "Zodiac, sun, moon and planet glyphs each sit in a single character, so they slot neatly into a bio, a chart label or a horoscope post. These examples show a sign in a bio, a sun/moon pair, a placement, a date range and a compatibility line.",
      "Copy a line and swap in your own sign or placement. Pair a sign with its ruling planet for a fuller reading, or tag a post with just your sign.",
    ],
    examples: [
      { text: "♌ Leo · she/her", label: "Sign in a bio" },
      { text: "Sun ☉ Leo  ·  Moon ☽ Cancer", label: "Sun / moon pair" },
      { text: "♀ Venus in ♎ Libra", label: "Chart placement" },
      { text: "♓ Pisces — today's reading", label: "Horoscope header" },
      { text: "♈ Aries ♥ ♐ Sagittarius", label: "Compatibility" },
      { text: "♑ Capricorn (Dec 22 – Jan 19)", label: "Sign with dates" },
      { text: "♈♉♊♋♌♍♎♏♐♑♒♓", label: "All twelve signs" },
      { text: "☿ Mercury · ♂ Mars · ♃ Jupiter", label: "Planet row" },
      { text: "♏ Scorpio season ✦", label: "Season post" },
      { text: "Rising ♍ Virgo", label: "Rising sign" },
      { text: "♃ Jupiter in ♐ Sagittarius", label: "Transit" },
      { text: "☉ ♌ · ☽ ♋ · Asc ♍", label: "Big three" },
      { text: "Full Moon ☽ in ♒", label: "Moon phase" },
      { text: "♀ Venus ♥ ♂ Mars", label: "Venus & Mars" },
      { text: "☿ retrograde ⚠", label: "Retrograde" },
      { text: "★ daily horoscope ★", label: "Daily header" },
    ],
    symbols: ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓", "⛎", "☉", "☽", "♀", "♂"],
    related: [
      { kind: "category", slug: "zodiac-symbols", label: "Zodiac" },
      { kind: "category", slug: "planet-symbols", label: "Planets" },
    ],
  },
  {
    slug: "gaming-chat",
    name: "Gaming & stream examples",
    tagline: "Copy-paste game-chat lines — HP ♥♥♥, Rank ★★★, ☠ You died.",
    intro: [
      "Symbols give game chat, stream titles and usernames a quick visual edge. These examples show a health bar, a rank, a versus line, a danger callout and a stream title — the kind of shorthand that reads at a glance in a fast chat.",
      "Copy a line and make it yours. Mixing a couple of contrasting marks — a heart and a skull, say — reads better than crowding several together.",
    ],
    examples: [
      { text: "HP ♥♥♥♡♡", label: "Health bar" },
      { text: "Rank ★★★☆☆", label: "Rank / level" },
      { text: "Red ⚔ Blue", label: "Versus" },
      { text: "☠ You died", label: "Defeat callout" },
      { text: "⚡ Speed boost!", label: "Power-up" },
      { text: "✦ RANKED GRIND ✦", label: "Stream title" },
      { text: "⚙ Settings · ☢ Hazard zone", label: "Menu / labels" },
      { text: "♛ Winner: player_one", label: "Crown the winner" },
      { text: "GG ♛", label: "Good game" },
      { text: "Lvl 80 ★★★★☆", label: "Level" },
      { text: "⚔ PvP enabled", label: "PvP" },
      { text: "☢ Danger zone ahead", label: "Hazard" },
      { text: "✦ MVP ✦", label: "MVP" },
      { text: "♥ x3 lives left", label: "Lives" },
      { text: "★ Achievement unlocked", label: "Achievement" },
      { text: "⚡ Combo x5!", label: "Combo" },
    ],
    symbols: ["♥", "★", "⚔", "☠", "⚡", "✦", "➜", "⚙", "☢", "☣", "♛", "✚"],
    related: [
      { kind: "category", slug: "warning-symbols", label: "Warning" },
      { kind: "category", slug: "star-symbols", label: "Stars" },
    ],
  },
];

/**
 * Old (pre-rework) use-case slugs → the category that now best replaces them.
 * The route 301-redirects these so already-indexed URLs don't 404.
 */
export const USE_CASE_REDIRECTS: Record<string, string> = {
  "music-notes": "music-symbols",
  "chess-pieces": "chess-symbols",
  "card-suits": "card-dice-symbols",
  "zodiac-signs": "zodiac-symbols",
  "bullet-points": "bullet-symbols",
  "rating-stars": "star-symbols",
  "arrow-pointers": "arrow-symbols",
  "math-equations": "math-symbols",
  "love-and-romance": "heart-symbols",
  "weather-and-seasons": "weather-symbols",
  "keyboard-shortcuts": "keyboard-symbols",
  "checkmarks-and-crosses": "check-cross-symbols",
};

export function getUseCase(slug: string): UseCase | undefined {
  return useCases.find((u) => u.slug === slug);
}

/** Resolve a use case's reference symbols to indexed records (drops any not in the index). */
export function resolveUseCaseSymbols(uc: UseCase) {
  return uc.symbols
    .map((sym) => {
      const slug = SYMBOL_SLUG_MAP[sym];
      if (!slug) return null;
      return { symbol: sym, slug, name: getSymbolName(sym) };
    })
    .filter((x): x is { symbol: string; slug: string; name: string } => x !== null);
}
